/** @jsx jsx */
import { jsx } from "@emotion/react";
import { FC, ReactNode, useMemo } from "react";
import noop from "lodash/fp/noop";
import { FlowioTableCellInputProps } from "../FlowioInput";
import { TableCellValue } from "../../../../requests/table/types";
import { alignSelfCss, entitiesContainerCss } from "./styles";
import useCellArrayActions from "./useCellArrayActions";
import SingleCellArray from "./SingleTableCellInput";
import { AddRowButton } from "../EwaButtons";

export type TableCellArrayInputProps = {
  values: TableCellValue[];
  onChange: (v: TableCellValue[]) => void;
  allowEdit?: boolean;
  allowInsert?: boolean;
  disabled?: boolean;
} & Omit<FlowioTableCellInputProps, "editable" | "value" | "onChange">;

const TableCellArrayInput: FC<TableCellArrayInputProps> = ({
  values: tableCellValues,
  disabled,
  onChange = noop,
  allowEdit = false,
  allowInsert = false,
  ...rest
}) => {
  const values = useMemo(() => tableCellValues.map(Number), [tableCellValues]);

  const { handleAdd, handleEdit, handleDelete } = useCellArrayActions(
    values,
    onChange
  );

  const renderVal = (val: number, index: number): ReactNode => {
    const isEdit = !disabled && allowEdit;
    const selectValue = val === -1 ? null : val;

    const key = Math.random().toString(36).substring(7);

    return (
      <SingleCellArray
        {...rest}
        key={key}
        isEdit={isEdit}
        editable={isEdit}
        value={selectValue}
        handleEdit={(newVal): void => handleEdit(newVal, index)}
        handleDelete={(): void => handleDelete(index)}
      />
    );
  };

  return (
    <div css={entitiesContainerCss}>
      {values.map((val, index) => renderVal(val, index))}
      {allowInsert && !disabled && (
        <div css={alignSelfCss}>
          <AddRowButton onClick={handleAdd} />
        </div>
      )}
    </div>
  );
};

export default TableCellArrayInput;
